import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { api } from './lib/api';
import { GUIDED_ORDER, GUIDED_SCRIPTS, type SectionScript } from './guidedScripts';
import { IconBack, IconClose } from './components/icons';
import { StagedLoading } from './components/StagedLoading';
// cc-11 round 14 (Sol blocker 2) — the standing quantity note the stage-1 prompt promises under EVERY quantity
// question. The guided reflection renders /api/classify output, so the promise has to hold here too.
import { isQuantityAsk, QUANTITY_HINT_COPY } from './quantityHint';

// Polish B2 reliability tranche — most classify checks resolve well under 2s, so a staged-loader would just
// flicker; this hook flips true only once `active` has held for `delayMs`, so the honest "still reading" copy
// appears ONLY for the guided classify waits that actually run long.
function useSlowWait(active: boolean, delayMs = 2000): boolean {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!active) { setSlow(false); return; }
    const t = setTimeout(() => setSlow(true), delayMs);
    return () => clearTimeout(t);
  }, [active, delayMs]);
  return slow;
}
const CHECK_STAGES = ['Reading your line…', 'Still reading — thanks for your patience…'];

// Conversational authoring Phases 1–4 — the CONVERSATION RAIL: a client state machine that walks the CV section by
// section, asking one question at a time, over the EXISTING engine (classify / entries / bullets / model). Two modes:
//   AUTHOR — build entry by entry (fields → write-a-line loop → save), every section skippable.
//   REFINE — seed-from-import: step through parsed entries bullet by bullet, classify on demand, ask, let the user
//            revise (their words) or keep.
// FIREWALL: the tool asks and structures; the USER writes every word. The classifier returns diagnosis+question only
// (server-sanitized); this component renders them as the consultant's reflection and NEVER writes bullet text itself.
// THE WRITER IS IN CHARGE: after any diagnosis, "Keep it as written" is always available — guidance, never a wall.
// PAYMENT: saves/edits stay craft-gated; a 402 pauses the conversation at a resumable point (onPaywall opens the
// modal; a reloadKey bump after unlock auto-retries the pending write).

interface Section { id: string; name: string; archetype: string }
interface SignalDTO {
  id: string;
  plain_language: string;
  what_counts: string;
  elicitation_question: string;
  suggested_section_ids: string[];
  provenance: { claim_kind: string; source: string };
}
interface Gate { gate_id: string; status: 'pass' | 'flag'; diagnosis: string; question: string }
interface ClassifyResult { overall: 'pass' | 'needs_attention'; ladder: Gate; integrity: Gate[] }
interface MBullet { id: string; text: string }
interface MEntry { id: string; section: string; archetype: 'experience' | 'credential'; title: string | null; organization: string | null; dateText: string; bullets: MBullet[] }
interface Model { sections: { id: string; name: string; entries: MEntry[] }[] }
// `sectionId` is the entry's TAXONOMY section id, taken from the entry itself — NOT the enclosing model section's
// id, which is a synthetic merged-heading id (`out:<label>`). `sectionName` remains the display heading.
interface RefItem { entryId: string; sectionId: string; sectionName: string; archetype: 'experience' | 'credential'; title: string; bullets: MBullet[] }

export function GuidedAuthor({ mode, cvId, specialtyId, specialtyName, stream, sections, reloadKey, onCvMinted, onPaywall, onFinish, onClassicForm, onSaved }: {
  mode: 'author' | 'refine';
  cvId: string;                       // author mode: '' until the first save mints the CV
  specialtyId: string;
  specialtyName: string;
  stream: string;                     // '' | 'img' | 'cmg' — gates the IMG-stream weave notes
  sections: Section[];
  reloadKey: number;                  // bumped by the paywall's unlock — retries the pending write
  onCvMinted: (cvId: string) => void;
  onPaywall: (cvId: string | null) => void;
  onFinish: (cvId: string) => void;   // hand off to the read
  onClassicForm: () => void;          // escape hatch — the standard editor
  onSaved?: (entryId: string) => void; // Phase 5 — refresh + highlight signal for the live CV pane
}) {
  // The walk: blueprint-ordered scripts, filtered to sections the server actually has.
  const walk: SectionScript[] = GUIDED_ORDER
    .filter((id) => sections.some((s) => s.id === id) && GUIDED_SCRIPTS[id])
    .map((id) => GUIDED_SCRIPTS[id]);

  // ---- shared conversation state ----
  const [secIdx, setSecIdx] = useState(0);
  const [stage, setStage] = useState<'intro' | 'fields' | 'lines' | 'more' | 'done'>('intro');
  const [fieldIdx, setFieldIdx] = useState(0);
  const [fieldVals, setFieldVals] = useState<Record<string, string>>({});
  const [lines, setLines] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [check, setCheck] = useState<ClassifyResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkNote, setCheckNote] = useState('');      // degrade-open note when the check itself fails
  const [saving, setSaving] = useState(false);
  const [pendingSave, setPendingSave] = useState(false); // a 402'd write awaiting unlock
  const [savedTotal, setSavedTotal] = useState(0);
  const [error, setError] = useState('');
  const liveCvId = useRef(cvId);
  const checkingSlow = useSlowWait(checking); // Polish B2 — the honest "still reading" line, only past 2s

  // Signal-aware guided generation — the ratified specialty signal set, fetched once. Failure/empty means the
  // feature is simply absent (zero UI) — never an error surfaced to the writer.
  const [signals, setSignals] = useState<SignalDTO[]>([]);
  const [expandedSignalId, setExpandedSignalId] = useState<string | null>(null);
  const [showMoreChips, setShowMoreChips] = useState(false);
  const lineHintIdx = useRef(0); // rotates over the section-relevant signals; advances only on an accepted line
  useEffect(() => {
    if (!specialtyId) return;
    let cancelled = false;
    void (async () => {
      const r = await api<{ signals: SignalDTO[] }>(`/api/specialties/${specialtyId}/signals`);
      if (cancelled) return;
      setSignals(r.ok && Array.isArray(r.data?.signals) ? r.data.signals : []);
    })();
    return () => { cancelled = true; };
  }, [specialtyId]);

  // ---- refine-mode state ----
  const [refItems, setRefItems] = useState<RefItem[] | null>(null);
  const [refIdx, setRefIdx] = useState(0);
  const [refBul, setRefBul] = useState(0);
  const [refStage, setRefStage] = useState<'intro' | 'bullet' | 'checked' | 'done'>('intro');
  const [refEdit, setRefEdit] = useState('');

  // Revenue P0 (P0-1) — checkout continuity for a paused write: GuidedAuthor owns pendingSave, so it persists its
  // OWN snapshot (keyed by cv id) rather than routing it through App/checkoutContext. On a 402-pause the in-progress
  // entry (fields + lines) is saved here; cleared on a successful save; restored on mount when `cvId` matches (e.g.
  // after the /checkout/success return trip re-renders this component with the same cv id and a bumped reloadKey).
  const pendingEntryKey = (id: string) => `cvcraft_pending_entry_${id}`;
  useEffect(() => {
    if (mode !== 'author' || !cvId) return;
    try {
      const raw = window.localStorage.getItem(pendingEntryKey(cvId));
      if (!raw) return;
      const snap = JSON.parse(raw) as { secIdx: number; fieldVals: Record<string, string>; lines: string[] };
      if (typeof snap.secIdx !== 'number') return;
      setSecIdx(snap.secIdx); setFieldVals(snap.fieldVals ?? {}); setLines(snap.lines ?? []); setStage('lines');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    } catch { /* best-effort restore only */ }
    // Only on mount / when the cv id first becomes known — not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cvId, mode]);

  // Keep the ref current when App restores a cv id later (e.g. the checkout-success return trip re-mounts with a
  // now-known cvId prop after it was minted mid-conversation).
  useEffect(() => { if (cvId) liveCvId.current = cvId; }, [cvId]);

  const script = walk[secIdx];
  const sec = sections.find((s) => s.id === script?.id);
  const archetype = sec?.archetype === 'credential' ? 'credential' : 'experience';
  const isExperience = archetype === 'experience';
  // Signal-aware guided generation — the signals suggesting the CURRENT section, in registry order (the order the
  // server already returns them in).
  const sectionSignals: SignalDTO[] = script ? signals.filter((s) => s.suggested_section_ids.includes(script.id)) : [];

  // REFINE — load the committed model once; flatten to entries that have bullets (those are the refinable words).
  useEffect(() => {
    if (mode !== 'refine' || !cvId) return;
    void (async () => {
      const r = await api<{ model: Model }>(`/api/cvs/${cvId}/model`);
      if (!r.ok) { setRefItems([]); return; }
      const items: RefItem[] = [];
      for (const s of r.data.model.sections) {
        for (const e of s.entries) {
          // sectionId comes from the ENTRY (`e.section`), not from `s.id` — `s.id` is a merged display heading.
          if (e.bullets.length > 0) items.push({ entryId: e.id, sectionId: e.section, sectionName: s.name, archetype: e.archetype, title: e.title ?? s.name, bullets: e.bullets });
        }
      }
      setRefItems(items);
    })();
  }, [mode, cvId]);

  // Paywall unlock signal → retry the paused write.
  useEffect(() => {
    if (!pendingSave) return;
    if (mode === 'refine') void saveRevision(); else void saveEntry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  function resetEntry() { setFieldIdx(0); setFieldVals({}); setLines([]); setDraft(''); setCheck(null); setCheckNote(''); setError(''); }
  function nextSection() {
    resetEntry();
    setExpandedSignalId(null); setShowMoreChips(false); lineHintIdx.current = 0; // fresh chip state per section
    if (secIdx + 1 < walk.length) { setSecIdx(secIdx + 1); setStage('intro'); } else setStage('done');
  }

  // The write-a-line loop: classify the draft; a pass ACCEPTS it, a flag becomes the consultant's reflection.
  // A failed check degrades OPEN (the line stays addable) — the check serves the writer, never gates them.
  async function checkLine() {
    const text = draft.trim();
    if (!text) return;
    setChecking(true); setCheck(null); setCheckNote(''); setError('');
    const r = await api<{ result: ClassifyResult }>('/api/classify', { method: 'POST', body: JSON.stringify({ bullet_text: text, section: script.id, archetype }) });
    setChecking(false);
    if (!r.ok) { setCheckNote('The check didn’t run this time — you can add the line anyway and review later.'); return; }
    if (r.data.result.overall === 'pass') { acceptLine(); setCheckNote('That reads clearly — added.'); }
    else setCheck(r.data.result);
  }
  function acceptLine() {
    const t = draft.trim(); if (!t) return;
    setLines((l) => [...l, t]); setDraft(''); setCheck(null);
    if (sectionSignals.length > 0) lineHintIdx.current = (lineHintIdx.current + 1) % sectionSignals.length; // advance only on accept
  }

  async function saveEntry() {
    setSaving(true); setError('');
    const body: Record<string, unknown> = {
      specialty_id: specialtyId, section: script.id,
      title: fieldVals.title ?? '', organization: fieldVals.organization ?? '', date_text: fieldVals.dateText ?? '',
      start_date: '', end_date: '', bullets: lines,
    };
    if (liveCvId.current) body.cv_id = liveCvId.current;
    else if (stream) body.stream = stream; // stream rides CV creation (server contract: set-on-creation only)
    const r = await api<{ cv_id: string; entry_id?: string; reason?: string }>('/api/entries', { method: 'POST', body: JSON.stringify(body) });
    setSaving(false);
    if (r.status === 402) {
      const d = r.data as { cv_id?: string } | null;
      if (d?.cv_id && !liveCvId.current) { liveCvId.current = d.cv_id; onCvMinted(d.cv_id); }
      const targetCv = d?.cv_id ?? liveCvId.current;
      // Revenue P0 (P0-1) — persist the in-progress entry so a checkout round-trip (window.location replaces the
      // page) doesn't lose it; GuidedAuthor's own mount-effect restores it when this cv id is seen again.
      if (targetCv) {
        try { window.localStorage.setItem(pendingEntryKey(targetCv), JSON.stringify({ secIdx, fieldVals, lines })); } catch { /* best-effort */ }
      }
      setPendingSave(true); onPaywall(targetCv ?? null); return;
    }
    if (!r.ok) { setError('That didn’t save — check your connection and try again.'); return; }
    setPendingSave(false);
    if (r.data.cv_id && !cvId) onCvMinted(r.data.cv_id);
    liveCvId.current = r.data.cv_id ?? liveCvId.current;
    if (liveCvId.current) { try { window.localStorage.removeItem(pendingEntryKey(liveCvId.current)); } catch { /* best-effort */ } }
    setSavedTotal((n) => n + 1);
    if (r.data.entry_id) onSaved?.(r.data.entry_id); // the pane refreshes + highlights the new entry
    resetEntry(); setStage('more');
  }

  // REFINE — persist the user's revision of the current bullet (their words; the existing user-author write).
  async function saveRevision() {
    if (!refItems) return;
    const b = refItems[refIdx].bullets[refBul];
    const text = refEdit.trim();
    if (!text || text === b.text) { refNext(); return; }
    setSaving(true); setError('');
    const r = await api(`/api/bullets/${b.id}`, { method: 'PATCH', body: JSON.stringify({ text }) });
    setSaving(false);
    if (r.status === 402) { setPendingSave(true); onPaywall(cvId); return; }
    if (!r.ok) { setError('That didn’t save — check your connection and try again.'); return; }
    setPendingSave(false);
    setRefItems(refItems.map((it, i) => i !== refIdx ? it : { ...it, bullets: it.bullets.map((bb, j) => j !== refBul ? bb : { ...bb, text }) }));
    onSaved?.(refItems[refIdx].entryId); // the pane reflects the revision
    refNext();
  }

  async function refCheckBullet() {
    if (!refItems) return;
    const it = refItems[refIdx]; const b = it.bullets[refBul];
    setChecking(true); setCheck(null); setCheckNote(''); setError('');
    // cc-11 round 18 (Sol round-17 blocker 1) — SEND THE SECTION. This call used to omit it even though the entry
    // knows it; `/api/classify` then coerced it to '' and the prompt rendered "section: unspecified", falsifying the
    // prompt's own "always its section" AND disarming the research null-router (which arms only on
    // section === 'research-and-scholarly-activity'), so a refined research bullet could never get the reroute the
    // applicant_action claims promise.
    const r = await api<{ result: ClassifyResult }>('/api/classify', { method: 'POST', body: JSON.stringify({ bullet_text: b.text, section: it.sectionId, archetype: it.archetype }) });
    setChecking(false);
    setRefStage('checked');
    if (!r.ok) { setCheckNote('The check didn’t run this time — keep going; you can come back to this line.'); return; }
    if (r.data.result.overall === 'pass') setCheckNote('This one reads clearly — nothing to ask.');
    else { setCheck(r.data.result); setRefEdit(b.text); }
  }
  function refNext() {
    if (!refItems) return;
    setCheck(null); setCheckNote(''); setRefEdit(''); setRefStage('bullet');
    const it = refItems[refIdx];
    if (refBul + 1 < it.bullets.length) { setRefBul(refBul + 1); return; }
    if (refIdx + 1 < refItems.length) { setRefIdx(refIdx + 1); setRefBul(0); setRefStage('intro'); return; }
    setRefStage('done');
  }
  function refSkipEntry() {
    if (!refItems) return;
    setCheck(null); setCheckNote(''); setRefEdit(''); setRefStage(refIdx + 1 < refItems.length ? 'intro' : 'done');
    if (refIdx + 1 < refItems.length) { setRefIdx(refIdx + 1); setRefBul(0); }
  }

  // The classifier's reflection, rendered as the consultant's follow-up — its own sanitized words only.
  const reflection = (c: ClassifyResult): ReactNode => {
    const gates = [...(c.ladder.status === 'flag' ? [c.ladder] : []), ...c.integrity];
    return (
      <div style={S.reflect} data-testid="guided-reflection">
        {gates.map((g) => (
          <div key={g.gate_id}>
            <p style={S.reflectItem} data-testid={`guided-gate-${g.gate_id}`}>
              <em>{g.diagnosis}</em><br />{g.question}
            </p>
            {isQuantityAsk(g.question) && <p style={S.quantityHint} data-testid="quantity-hint">{QUANTITY_HINT_COPY}</p>}
          </div>
        ))}
      </div>
    );
  };

  const progress = mode === 'author' && script
    ? `Section ${secIdx + 1} of ${walk.length} — ${sec?.name ?? script.id}${savedTotal ? ` · ${savedTotal} ${savedTotal === 1 ? 'entry' : 'entries'} saved` : ''}`
    : '';

  const paused = !pendingSave ? null : (
    <div style={S.pause} data-testid="guided-paused">
      <p style={S.help}>Your words are kept — this CV needs its unlock to save. Once that’s done:</p>
      <button className="btn btn-primary" disabled={saving} onClick={() => (mode === 'refine' ? void saveRevision() : void saveEntry())} data-testid="guided-retry">
        {saving ? 'Saving…' : 'Try again'}
      </button>
    </div>
  );

  // ================= REFINE MODE =================
  if (mode === 'refine') {
    if (refItems === null) return <main style={S.main} data-testid="guided-author"><p style={S.help}>Reading what you brought in…</p></main>;
    if (refItems.length === 0 || refStage === 'done') return (
      <main style={S.main} data-testid="guided-author">
        <h1 style={S.q}>{refItems.length === 0 ? 'Everything’s in.' : 'That’s the walk-through.'}</h1>
        <p style={S.help}>{refItems.length === 0 ? 'Nothing here needs a line-by-line pass.' : 'Every line we looked at is yours — kept or revised in your own words.'}</p>
        <div style={S.bar}>
          <button className="btn btn-primary" onClick={() => onFinish(cvId)} data-testid="guided-finish">See how it reads <IconBack aria-hidden="true" style={{ transform: 'rotate(180deg)' }} /></button>
          <button className="btn btn-ghost" onClick={onClassicForm} data-testid="guided-to-form">Open the editor instead</button>
        </div>
      </main>
    );
    const it = refItems[refIdx]; const b = it.bullets[refBul];
    return (
      <main style={S.main} data-testid="guided-author">
        <p style={S.progress} data-testid="guided-progress">Entry {refIdx + 1} of {refItems.length} — {it.title} <span style={{ color: 'var(--muted)' }}>({it.sectionName})</span></p>
        {refStage === 'intro' && (
          <div>
            <h1 style={S.q}>{refIdx === 0 ? 'I’ve read what you brought — let’s go through it together.' : `Next: ${it.title}.`}</h1>
            <p style={S.help}>{it.bullets.length} {it.bullets.length === 1 ? 'line' : 'lines'} here. I’ll read each one and ask only where a reviewer would want more. You decide every word.</p>
            <div style={S.bar}>
              <button className="btn btn-primary" onClick={() => setRefStage('bullet')} data-testid="guided-ref-start">Look at this entry</button>
              <button className="btn btn-ghost" onClick={refSkipEntry} data-testid="guided-ref-skip-entry">Skip this entry</button>
              <button className="btn btn-ghost" onClick={() => setRefStage('done')} data-testid="guided-ref-finish-early">That’s enough — run my review</button>
            </div>
          </div>
        )}
        {(refStage === 'bullet' || refStage === 'checked') && (
          <div>
            <blockquote style={S.quote} data-testid="guided-ref-bullet">{b.text}</blockquote>
            {refStage === 'bullet' && (
              <div style={S.bar}>
                <button className="btn btn-primary" onClick={() => void refCheckBullet()} disabled={checking} data-testid="guided-ref-check">{checking ? 'Reading…' : 'Read this line'}</button>
                <button className="btn btn-ghost" onClick={refNext} data-testid="guided-ref-next">Next line</button>
                <button className="btn btn-ghost" onClick={refSkipEntry} data-testid="guided-ref-skip-entry">Skip this entry</button>
              </div>
            )}
            {checkingSlow && <StagedLoading stages={CHECK_STAGES} intervalMs={4000} variant="inline" testId="guided-slow-wait" subtext={null} />}
            {refStage === 'checked' && (
              <div>
                {checkNote && <p style={S.note} data-testid="guided-check-note">{checkNote}</p>}
                {check && reflection(check)}
                {check && (
                  <label style={S.label}>Revise it in your own words — or keep it as it is.
                    <textarea style={S.input} rows={3} value={refEdit} onChange={(e) => setRefEdit(e.target.value)} data-testid="guided-ref-edit" />
                  </label>
                )}
                {paused ?? (
                  <div style={S.bar}>
                    {check && <button className="btn btn-primary" onClick={() => void saveRevision()} disabled={saving} data-testid="guided-ref-save">{saving ? 'Saving…' : 'Save my revision'}</button>}
                    <button className="btn btn-secondary" onClick={refNext} data-testid="guided-ref-keep">{check ? 'Keep it as written' : 'Next line'}</button>
                  </div>
                )}
                {error && <p style={S.err} data-testid="guided-error">{error}</p>}
              </div>
            )}
          </div>
        )}
      </main>
    );
  }

  // ================= AUTHOR MODE =================
  if (stage === 'done') return (
    <main style={S.main} data-testid="guided-author">
      <h1 style={S.q}>That’s every section.</h1>
      {/* COPY CONTRACT (Sol-ratified 2026-08-07) — "where it's strong" advertised a strength verdict the read does
          not issue; the read establishes coverage and thinness. Aligned with the corrected landing + Help copy. */}
      <p style={S.help}>{savedTotal} {savedTotal === 1 ? 'entry' : 'entries'} on your {specialtyName} CV — every word yours. The honest next step is a read: what your CV already covers and where it looks thin.</p>
      <div style={S.bar}>
        <button className="btn btn-primary" onClick={() => liveCvId.current && onFinish(liveCvId.current)} disabled={!liveCvId.current} data-testid="guided-finish">See how it reads <IconBack aria-hidden="true" style={{ transform: 'rotate(180deg)' }} /></button>
        <button className="btn btn-ghost" onClick={onClassicForm} data-testid="guided-to-form">Open the editor instead</button>
      </div>
      {!liveCvId.current && <p style={S.note}>Nothing saved yet — step back and add at least one entry first.</p>}
    </main>
  );

  if (!script) return null;
  const fq = script.fields[fieldIdx];

  return (
    <main style={S.main} data-testid="guided-author">
      <p style={S.progress} data-testid="guided-progress">{progress}</p>

      {stage === 'intro' && (
        <div>
          <h1 style={S.q}>{sec?.name ?? script.id}</h1>
          <p style={S.help}>{script.intro}</p>
          {stream === 'img' && script.imgNote && <p style={S.imgNote} data-testid="guided-img-note">{script.imgNote}</p>}
          <p style={S.standard} data-testid="guided-standard">{script.standard}</p>
          {sectionSignals.length > 0 && (
            <div style={S.signalBlock} data-testid="gs-chips">
              <p style={S.signalHeading}>Questions to help you recall relevant {specialtyName} experience</p>
              <div style={S.chipRow}>
                {(showMoreChips ? sectionSignals : sectionSignals.slice(0, 3)).map((s) => {
                  const isOpen = expandedSignalId === s.id;
                  return (
                    <div key={s.id} style={S.chipWrap}>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        style={S.chip}
                        onClick={() => setExpandedSignalId(isOpen ? null : s.id)}
                        data-testid={`gs-chip-${s.id}`}
                        aria-expanded={isOpen}
                      >
                        {s.plain_language}
                      </button>
                      {isOpen && (
                        <div style={S.chipOpen} data-testid={`gs-chip-open-${s.id}`}>
                          <p style={S.help}>{s.what_counts}</p>
                          <p style={S.chipQuestion}><strong>{s.elicitation_question}</strong></p>
                          <p style={S.provenance}>Basis: {s.provenance.source}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {!showMoreChips && sectionSignals.length > 3 && (
                <button type="button" className="btn btn-ghost" style={S.showMore} onClick={() => setShowMoreChips(true)} data-testid="gs-show-more">
                  Show more
                </button>
              )}
            </div>
          )}
          <div style={S.bar}>
            <button className="btn btn-primary" onClick={() => setStage('fields')} data-testid="guided-add-entry">Add an entry</button>
            <button className="btn btn-ghost" onClick={nextSection} data-testid="guided-skip-section">Nothing to add — next section</button>
          </div>
        </div>
      )}

      {stage === 'fields' && fq && (
        <form onSubmit={(e) => {
          e.preventDefault();
          if (!(fieldVals[fq.field] ?? '').trim() && !fq.optional) return;
          if (fieldIdx + 1 < script.fields.length) setFieldIdx(fieldIdx + 1);
          else setStage('lines'); // both archetypes get the line step (optional for credentials)
        }}>
          <h1 style={S.q}>{fq.q}</h1>
          <p style={S.help}>{fq.help}</p>
          <input style={S.input} autoFocus value={fieldVals[fq.field] ?? ''} onChange={(e) => setFieldVals({ ...fieldVals, [fq.field]: e.target.value })} data-testid={`guided-field-${fq.field}`} />
          <div style={S.bar}>
            <button className="btn btn-primary" type="submit" disabled={!(fieldVals[fq.field] ?? '').trim() && !fq.optional} data-testid="guided-continue">Continue</button>
            {fq.optional && <button className="btn btn-ghost" type="button" onClick={() => { if (fieldIdx + 1 < script.fields.length) setFieldIdx(fieldIdx + 1); else setStage('lines'); }} data-testid="guided-skip-field">Skip</button>}
          </div>
        </form>
      )}

      {stage === 'lines' && (
        <div>
          <h1 style={S.q}>{script.bulletQ}</h1>
          <p style={S.help}>{script.bulletHelp}</p>
          {sectionSignals.length > 0 && (
            <p style={S.lineHint} data-testid="gs-line-hint">{sectionSignals[lineHintIdx.current % sectionSignals.length].elicitation_question}</p>
          )}
          {lines.length > 0 && (
            <ul style={S.lineList} data-testid="guided-lines">
              {lines.map((l, i) => (
                <li key={i} style={S.lineItem}>{l} <button className="btn btn-ghost" style={S.lineDrop} onClick={() => setLines(lines.filter((_, j) => j !== i))} aria-label="Remove line" data-testid={`guided-drop-${i}`}><IconClose aria-hidden="true" /></button></li>
              ))}
            </ul>
          )}
          <textarea style={S.input} rows={3} value={draft} onChange={(e) => { setDraft(e.target.value); }} placeholder="Your words — one line at a time" data-testid="guided-draft" />
          {checkNote && <p style={S.note} data-testid="guided-check-note">{checkNote}</p>}
          {check && (
            <div>
              {reflection(check)}
              <div style={S.bar}>
                <button className="btn btn-primary" onClick={() => void checkLine()} disabled={checking || !draft.trim()} data-testid="guided-recheck">{checking ? 'Reading…' : 'I’ve revised it — check again'}</button>
                <button className="btn btn-secondary" onClick={acceptLine} data-testid="guided-keep">Keep it as written</button>
              </div>
            </div>
          )}
          {checkingSlow && <StagedLoading stages={CHECK_STAGES} intervalMs={4000} variant="inline" testId="guided-slow-wait" subtext={null} />}
          {!check && (
            <div style={S.bar}>
              <button className="btn btn-primary" onClick={() => void checkLine()} disabled={checking || !draft.trim()} data-testid="guided-check">{checking ? 'Reading…' : 'Check this line'}</button>
              {checkNote && draft.trim() && <button className="btn btn-secondary" onClick={acceptLine} data-testid="guided-add-unchecked">Add it anyway</button>}
            </div>
          )}
          {paused ?? (
            <div style={{ ...S.bar, marginTop: 'var(--space-5)' }}>
              <button className="btn btn-secondary" onClick={() => void saveEntry()} disabled={saving || (isExperience && lines.length === 0)} data-testid="guided-save-entry">
                {saving ? 'Saving…' : 'Save this entry'}
              </button>
              {isExperience && lines.length === 0 && <span style={S.hint}>Add at least one line first — it’s what a reviewer reads.</span>}
            </div>
          )}
          {error && <p style={S.err} data-testid="guided-error">{error}</p>}
        </div>
      )}

      {stage === 'more' && (
        <div>
          <h1 style={S.q}>Saved.</h1>
          <p style={S.help}>{script.askAnother}</p>
          <div style={S.bar}>
            <button className="btn btn-primary" onClick={() => { resetEntry(); setStage('fields'); }} data-testid="guided-another">Add another</button>
            <button className="btn btn-secondary" onClick={nextSection} data-testid="guided-next-section">Next section <IconBack aria-hidden="true" style={{ transform: 'rotate(180deg)' }} /></button>
          </div>
        </div>
      )}

      <p style={S.skipLine}>
        Prefer a form? <button className="btn btn-ghost" style={S.skipBtn} onClick={onClassicForm} data-testid="guided-skip">Use the standard editor</button>
      </p>
    </main>
  );
}

const S: Record<string, CSSProperties> = {
  main: { maxWidth: 640, margin: '40px auto', padding: '0 var(--space-4)', fontFamily: 'var(--sans)', lineHeight: 'var(--lh-body)' },
  progress: { fontSize: 'var(--fs-small)', color: 'var(--muted)', margin: '0 0 var(--space-4)' },
  q: { fontSize: 'var(--fs-h1)', margin: '0 0 var(--space-1)' },
  help: { color: 'var(--muted)', margin: 'var(--space-1) 0 var(--space-4)' },
  standard: { background: 'var(--surface-subtle)', borderRadius: 6, padding: 'var(--space-3) var(--space-3)', fontSize: 'var(--fs-body)', margin: '0 0 var(--space-4)' },
  imgNote: { background: 'var(--surface-subtle)', borderLeft: '3px solid var(--ink-subtle)', borderRadius: 4, padding: 'var(--space-3) var(--space-3)', fontSize: 'var(--fs-body)', margin: '0 0 var(--space-3)' },
  input: { display: 'block', padding: 'var(--space-2)', fontSize: 15, marginTop: 'var(--space-1)', width: '100%', maxWidth: 560, boxSizing: 'border-box' },
  bar: { display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap', marginTop: 'var(--space-4)' },
  hint: { fontSize: 'var(--fs-small)', color: 'var(--muted)' },
  label: { display: 'block', margin: 'var(--space-3) 0', fontSize: 'var(--fs-body)' },
  reflect: { borderLeft: '3px solid var(--warning)', background: 'var(--warn-surface)', borderRadius: 4, padding: 'var(--space-3) var(--space-3)', margin: 'var(--space-3) 0 0' },
  reflectItem: { margin: 'var(--space-1) 0', fontSize: 'var(--fs-body)' },
  // cc-11 round 14 — same treatment as QualityReadView's `quantityHint`: quiet, italic, clearly product copy.
  quantityHint: { margin: 'calc(-1 * var(--space-1)) 0 var(--space-2)', color: 'var(--ink-subtle)', fontSize: 'var(--fs-small)', fontStyle: 'italic' },
  note: { fontSize: 'var(--fs-small)', color: 'var(--success)', margin: 'var(--space-2) 0 0' },
  quote: { borderLeft: '3px solid var(--rule)', margin: 'var(--space-3) 0', padding: 'var(--space-1) var(--space-3)', fontStyle: 'normal', fontSize: 15 },
  lineList: { margin: '0 0 var(--space-3)', paddingLeft: 20 },
  lineItem: { fontSize: 'var(--fs-body)', margin: 'var(--space-1) 0' },
  lineDrop: { fontSize: 'var(--fs-label)', padding: '0 var(--space-1)' },
  pause: { borderLeft: '3px solid var(--rule)', padding: '2px 0 2px var(--space-3)', marginTop: 'var(--space-4)' },
  skipLine: { marginTop: 'var(--space-6)', fontSize: 'var(--fs-small)', color: 'var(--muted)' },
  skipBtn: { fontSize: 'var(--fs-small)', padding: '0 2px' },
  err: { color: 'var(--danger)', marginTop: 'var(--space-3)' },
  signalBlock: { background: 'var(--surface-subtle)', borderRadius: 6, padding: 'var(--space-3) var(--space-3)', margin: '0 0 var(--space-4)' },
  signalHeading: { fontSize: 'var(--fs-small)', color: 'var(--muted)', margin: '0 0 var(--space-2)', fontWeight: 600 },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' },
  chipWrap: { display: 'flex', flexDirection: 'column' as const },
  chip: { fontSize: 'var(--fs-small)', padding: 'var(--space-1) var(--space-3)' },
  chipOpen: { borderLeft: '3px solid var(--rule)', margin: 'var(--space-2) 0 0', padding: 'var(--space-2) var(--space-3)', maxWidth: 520 },
  chipQuestion: { margin: 'var(--space-1) 0', fontSize: 'var(--fs-body)' },
  provenance: { fontSize: 'var(--fs-label)', color: 'var(--muted)', margin: 'var(--space-1) 0 0' },
  showMore: { fontSize: 'var(--fs-small)', marginTop: 'var(--space-1)', padding: '0 2px', alignSelf: 'flex-start' },
  lineHint: { fontSize: 'var(--fs-small)', color: 'var(--muted)', margin: '-6px 0 var(--space-3)', fontStyle: 'italic' },
};
